import React, { useState, useMemo } from "react";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
  useTheme,
} from "@mui/material";
import { tokens } from "../../../theme";
import {
  useGetActionsQuery,
  useGetActionsByUserQuery,
} from "../../../features/action_log/actionLogApiSlice";

const AdminDashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [filter, setFilter] = useState("");
  const trimmed = filter.trim();

  /* ────── 1. Always call both hooks, but skip one of them ────── */
  const {
    data: allActions = [],
    isLoading: loadingAll,
    isError: errorAll,
  } = useGetActionsQuery({ limit: 500, skip: 0 });  // always load a recent slice for global stats

  const {
    data: userActions = [],
    isLoading: loadingUser,
    isError: errorUser,
  } = useGetActionsByUserQuery(
    { username: trimmed },
    { skip: !trimmed }                                             // skip if no username filter
  );

  /* ────── 2. Pick the data/states to display ────── */
  const actions = trimmed ? userActions : allActions;
  const isLoading = trimmed ? loadingUser : loadingAll;
  const isError = trimmed ? errorUser : errorAll;

  const rows = actions.map((row) => ({
    id: row._id,
    user: row.user.username,
    action: row.action,
    ts: new Date(row.createdAt).toLocaleString(),
  }));

  /* ────── 3. Unique users logged in today (global) ────── */
  const today = dayjs().startOf("day");
  const usersLoggedInToday = useMemo(() => {
    const seen = new Set();
    (allActions || []).forEach((a) => {
      if (!a) return;
      const isLogin = String(a.action).toUpperCase() === "LOGIN";
      if (!isLogin) return;
      if (!dayjs(a.createdAt).isSame(today, "day")) return;
      if (a.user?.username) {
        seen.add(a.user.username);
      }
    });
    return seen.size;
  }, [allActions, today]);

  return (
    <Box m="20px">
      <Typography variant="h2" color={colors.blueAccent[400]} gutterBottom>
        Admin Feed
      </Typography>
      <TextField
        variant="outlined"
        label="Filter by username"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        sx={{ mb: 3, width: 300, mr: 2}}
      />
      {/* ── stat bubble: users who logged in today ───────────────────── */}
      <Paper
        elevation={4}
        sx={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
          py: 2,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${colors.greenAccent[400]} 0%, ${colors.greenAccent[700]} 100%)`,
          color: colors.primary[900],
          minWidth: 180,
        }}
      >
        <Typography
          variant="caption"
          sx={{ opacity: 0.85, color: colors.primary[900], fontWeight: 600 }}
        >
          Users Logged In Today
        </Typography>
        <Typography
          variant="h3"
          sx={{ lineHeight: 1.1, fontWeight: 700, color: colors.primary[900] }}
        >
          {loadingAll ? "…" : usersLoggedInToday.toLocaleString()}
        </Typography>
        <Typography
          variant="caption"
          sx={{ opacity: 0.6, mt: 0.25, color: colors.primary[900] }}
        >
          {dayjs().format("MMM D, YYYY")}
        </Typography>
      </Paper>


      <Paper sx={{ maxHeight: "70vh", overflow: "auto" }}>
        <List>
          {isLoading && (
            <ListItem>
              <ListItemText primary="Loading…" />
            </ListItem>
          )}
          {isError && (
            <ListItem>
              <ListItemText primary="Error loading actions" />
            </ListItem>
          )}
          {rows.map((row) => (
            <ListItem key={row.id} divider>
              <ListItemText
                primary={row.action}
                secondary={`${row.user} • ${row.ts}`}
              />
            </ListItem>
          ))}
          {!isLoading && rows.length === 0 && (
            <ListItem>
              <ListItemText primary="No actions match this filter." />
            </ListItem>
          )}
        </List>
      </Paper>
    </Box>
  );
};

export default AdminDashboard;
